<script>
  // @ts-check
  import { onMount, tick } from "svelte";
  import { v4 as uuid } from "@lukeed/uuid";
  import Icon from "../../atoms/Icon.svelte";
  import DynamicHeading from "../../atoms/DynamicHeading.svelte";
  import DynamicHeadingContext from "../../atoms/DynamicHeadingContext.svelte";

  /** Primarily used for testing
   * @type {string}
   * */
  export let id = undefined;

  /** label - accordion header label
   *  @type {string}
   * */
  export let label;

  /** subtitle - accordion header subtitle
   *  @type {string}
   * */
  export let subtitle;

  /** The class to be applied to each accordion item
   *  @type {string}
   * */
  export let itemContainerClass = "";

  /** The class to be applied to the heading of the accordion item
   *  @type {string}
   * */
  export let itemHeadingClass = "";

  /** The class to be applied to the body of the slot item
   *  @type {string}
   * */
  export let itemBodySlotClass = "";

  /** Determines if the item is active or not
   *  @type {boolean}
   * */
  export let active = false;

  /** unique key for the accordion ids
   *  @type {string}
   * */
  let key = uuid();

  $: isOpen = active;

  const visibleClass = "overflow-visible visible";
  const hiddenClass = "overflow-hidden invisible";

  let overflowClass = hiddenClass;

  const handleToggle = () => {
    overflowClass = "overflow-hidden";
    isOpen = !isOpen;
  };

  onMount(() => {
    if (isOpen) {
      overflowClass = visibleClass;
    } else {
      overflowClass = hiddenClass;
    }
  });

  const collapseTransitionEnd = async () => {
    if (isOpen) {
      await tick();
      overflowClass = visibleClass;
    } else {
      overflowClass = hiddenClass;
    }
  };

  $: iconAnimationClass = isOpen && "-rotate-180";
  $: contentAnimationClass = isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]";
  $: contentOpacityClass = isOpen ? "opacity-[1]" : "opacity-[0]";

  $: contentItemAnimationClass = overflowClass;
</script>

<DynamicHeadingContext>
  <div
    {id}
    role="group"
    class="relative w-full border-t-thin border-black py-6 lg:py-8 xl:py-10 {itemContainerClass}"
  >
    <button
      aria-controls={`accordion-text-${key}`}
      aria-expanded={isOpen}
      class="group w-full outline-offset-2 outline-black focus-visible:outline-dashed {itemHeadingClass}"
      id={`accordion-title-${key}`}
      on:click={handleToggle}
      type="button"
    >
      <div class="flex flex-row items-center">
        <DynamicHeading
          class="heading-xs w-full text-left text-black group-hover:text-cobalt-blue group-focus-visible:text-cobalt-blue"
        >
          {label}
        </DynamicHeading>
        <Icon
          iconClass="w-[17px] h-[17px] group-hover:text-cobalt-blue transition-all transform duration-[300ms] ease-accordion-ease-out {iconAnimationClass}"
          name="caretDown"
        />
      </div>
      {#if subtitle}
        <p class="mt-4 text-left">Speakers: {subtitle}</p>
      {/if}
    </button>

    <div
      id={`accordion-text-${key}`}
      aria-labelledby={`accordion-title-${key}`}
      class="grid transition-all duration-[300ms] ease-accordion-ease-out {contentAnimationClass} {contentItemAnimationClass}"
      on:transitionend={collapseTransitionEnd}
      aria-hidden={!isOpen}
    >
      <div
        class="body-sm transition-all delay-[150ms] duration-[300ms] {contentItemAnimationClass} {contentOpacityClass}"
      >
        <div class="pt-6 text-black lg:pt-7 xl:pt-8 {itemBodySlotClass}">
          <slot />
        </div>
      </div>
    </div>
  </div>
</DynamicHeadingContext>
